import { useState } from "react";
import { ArrowDown, ArrowUp, Check, Pencil, Trash2, X } from "lucide-react";
import { fmtDate } from "@/lib/constants";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { TableCell, TableRow } from "@/components/ui/table";

const IconBtn = ({ onClick, disabled, title, testid, children, danger }) => (
  <button type="button" disabled={disabled} onClick={onClick} title={title} data-testid={testid}
    className={`p-1 rounded text-slate-400 disabled:opacity-20 transition-colors ${danger ? "hover:text-rose-600" : "hover:text-slate-800"}`}>{children}</button>
);

export const ChecklistRow = ({ it, i, count, today, onToggle, onDate, onRename, onMove, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(it.title);
  const done = it.status === "completed";
  const save = () => { onRename(i, draft); setEditing(false); };
  return (
    <TableRow data-testid={`cl-row-${i}`} className={done ? "bg-emerald-50/30" : ""}>
      <TableCell className="text-xs text-slate-400 tabular-nums align-top pt-3">{i + 1}</TableCell>
      <TableCell className="align-top">
        {editing ? (
          <div className="flex items-start gap-1">
            <Input autoFocus data-testid={`cl-title-${i}`} value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") { setDraft(it.title); setEditing(false); } }} className="h-8 text-sm bg-white" />
            <IconBtn onClick={save} title="Save" testid={`cl-save-${i}`}><Check className="h-4 w-4 text-emerald-600" /></IconBtn>
            <IconBtn onClick={() => { setDraft(it.title); setEditing(false); }} title="Cancel" testid={`cl-cancel-${i}`}><X className="h-4 w-4" /></IconBtn>
          </div>
        ) : (
          <span data-testid={`cl-title-${i}`} className={`block text-sm whitespace-normal break-words leading-snug ${done ? "text-slate-500 line-through decoration-slate-300" : "text-slate-800 font-medium"}`}>{it.title}</span>
        )}
      </TableCell>
      <TableCell className="text-center align-top pt-2.5">
        <Checkbox checked={done} onCheckedChange={(v) => onToggle(i, !!v)} data-testid={`cl-done-${i}`} className="h-5 w-5 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600" aria-label={`Mark ${it.title} ${done ? "incomplete" : "complete"}`} />
      </TableCell>
      <TableCell className="align-top">
        {done ? (
          <div>
            <Input type="date" max={today} value={it.completed_at || ""} onChange={(e) => onDate(i, e.target.value)} data-testid={`cl-completed-${i}`} className="h-8 text-xs bg-transparent border-transparent hover:border-slate-200 focus:bg-white w-[160px]" />
            {!it.completed_at && <div className="text-[11px] text-amber-700 mt-0.5 pl-1" data-testid={`cl-date-missing-${i}`}>Date not recorded</div>}
            {it.completed_at && <div className="sr-only">{fmtDate(it.completed_at)}</div>}
          </div>
        ) : <span className="text-slate-300 text-sm pl-1" data-testid={`cl-completed-${i}`}>—</span>}
      </TableCell>
      <TableCell className="align-top">
        <div className="flex items-center justify-end gap-0.5">
          <IconBtn disabled={i === 0} onClick={() => onMove(i, -1)} title="Move up" testid={`cl-up-${i}`}><ArrowUp className="h-3.5 w-3.5" /></IconBtn>
          <IconBtn disabled={i === count - 1} onClick={() => onMove(i, 1)} title="Move down" testid={`cl-down-${i}`}><ArrowDown className="h-3.5 w-3.5" /></IconBtn>
          <IconBtn onClick={() => { setDraft(it.title); setEditing(true); }} title="Edit name" testid={`cl-edit-${i}`}><Pencil className="h-3.5 w-3.5" /></IconBtn>
          <IconBtn onClick={() => onDelete(i)} title="Delete" testid={`cl-delete-${i}`} danger><Trash2 className="h-3.5 w-3.5" /></IconBtn>
        </div>
      </TableCell>
    </TableRow>
  );
};
